import React from 'react';

type MentoLogoWhiteProps = {
  width?: number;
  variant?: 'white' | 'dark';
  iconOnly?: boolean;
};

/**
 * White variant of Mento logo for landing page
 * Uses SVG assets from Figma with white triangle and white "Mento" text
 * The logo consists of:
 * - A white/sage triangle icon on the left (49x31)
 * - The text "Mento" in white on the right (123x32)
 * Height is automatically calculated to maintain aspect ratio
 */
export const MentoLogoWhite: React.FC<MentoLogoWhiteProps> = ({
  width = 180,
  variant = 'white',
  iconOnly = false,
}) => {
  const scale = width / 180;
  const scaledHeight = 32 * scale;
  const iconFill = variant === 'dark' ? '#505A54' : 'white';
  const textFill = variant === 'dark' ? '#1F2A37' : 'white';

  const icon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={iconOnly ? width : 49 * scale}
      height={iconOnly ? width * (31 / 49) : 31 * scale}
      viewBox="0 0 49 31"
      fill="none"
      style={{ flexShrink: 0 }}
    >
      <path d="M14.7068 30.8503H0L10.4915 0L22.1293 8.99759L14.7068 30.8503Z" fill={iconFill} />
      <path
        d="M25.1626 0.0117188L22.1289 9.00342L33.7101 17.959L29.291 30.8478H43.9321L48.394 17.947L25.1626 0.0117188Z"
        fill="#BDCCBE"
      />
    </svg>
  );

  if (iconOnly) return icon;

  return (
    <div
      role="img"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: `${8 * scale}px`,
        width: `${width}px`,
        height: `${scaledHeight}px`,
      }}
      aria-label="Mento"
    >
      {icon}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={123 * scale}
        height={32 * scale}
        viewBox="0 0 123 32"
        fill="none"
        style={{ flexShrink: 0 }}
      >
        <path
          d="M5.66673 30.9275V12.0175C5.84381 12.5477 6.3308 14.1383 7.17195 16.745L13.2371 30.9275H18.8153L24.8805 16.745L26.3857 12.0175V30.9275H32.0524V0H25.8987L16.0262 24.5653L6.15371 0H0V30.9275H5.66673Z"
          fill={textFill}
        />
        <path
          d="M57.4334 19.3518C57.4334 16.1265 56.4151 13.5197 54.4229 11.4432C52.4307 9.4108 49.863 8.39461 46.7197 8.39461C43.4879 8.39461 40.7431 9.45498 38.5295 11.6199C36.2717 13.7848 35.1649 16.5683 35.1649 19.9703C35.1649 23.3724 36.2717 26.1116 38.4853 28.2766C40.6546 30.4415 43.3108 31.5461 46.4541 31.5461C51.2797 31.5461 54.6886 29.4253 56.7693 25.1396L52.6078 22.7538C51.2797 25.4489 49.2432 26.7744 46.5426 26.7744C43.2223 26.7744 40.8759 24.6536 40.5217 21.2958H57.4334V19.3518ZM46.5426 12.6361C49.6859 12.6361 51.7667 14.3592 52.2979 17.231H40.5217C41.3629 14.5801 43.665 12.6361 46.5426 12.6361Z"
          fill={textFill}
        />
        <path
          d="M81.26 15.9939C81.26 11.399 78.2053 8.39461 73.5568 8.39461C70.4135 8.39461 67.9343 9.76426 66.1192 12.5477V9.01316H60.541V30.9275H66.1192V19.7936C66.1192 16.1265 68.2442 13.6081 71.2104 13.6081C74.1766 13.6081 75.6818 15.1545 75.6818 18.2914V30.9275H81.26V15.9939Z"
          fill={textFill}
        />
        <path
          d="M86.5432 13.5197V24.5653C86.5432 29.2044 88.8895 31.5461 93.5823 31.5461C94.9547 31.5461 96.1943 31.3251 97.3011 30.9275L96.7698 26.3326C96.1943 26.5093 95.5745 26.5976 94.9104 26.5976C93.051 26.5976 92.1213 25.5815 92.1213 23.5049V13.5197H96.9026V9.01316H92.1213V3.09275H86.5432V9.01316H83.1343V13.5197H86.5432Z"
          fill={textFill}
        />
        <path
          d="M119.142 11.4432C116.752 9.4108 113.918 8.39461 110.687 8.39461C107.455 8.39461 104.621 9.4108 102.275 11.4432C99.8844 13.5197 98.6891 16.3474 98.6891 19.9703C98.6891 23.5933 99.8844 26.4209 102.275 28.4533C104.621 30.5299 107.455 31.5461 110.687 31.5461C113.918 31.5461 116.752 30.5299 119.142 28.4533C121.489 26.4209 122.684 23.5933 122.684 19.9703C122.684 16.3474 121.489 13.5197 119.142 11.4432ZM106.259 24.5653C104.976 23.4165 104.356 21.8702 104.356 19.9703C104.356 18.0705 104.976 16.5241 106.259 15.3312C107.499 14.1825 108.96 13.6081 110.687 13.6081C112.413 13.6081 113.874 14.1825 115.158 15.3312C116.398 16.5241 117.017 18.0705 117.017 19.9703C117.017 21.8702 116.398 23.4165 115.158 24.5653C113.874 25.7582 112.413 26.3326 110.687 26.3326C108.96 26.3326 107.499 25.7582 106.259 24.5653Z"
          fill={textFill}
        />
      </svg>
    </div>
  );
};

export default MentoLogoWhite;
